'use client'
import { useEffect, useState } from "react";
import { useKindeBrowserClient } from "@kinde-oss/kinde-auth-nextjs";
import { useSearchParams } from "next/navigation";
import { fetchArticle, getArticlesAction, 
  // initialArticleGeneration, initialArticleGenerationAPI, 
  initialArticleGenerationStream, publishArticleAction, saveArticleAction } from "./actions";
import { Label } from '@/components/ui/label';
import { Input } from "@/components/ui/input";
import { Textarea } from '@/components/ui/textarea';
import { Button } from '@/components/ui/button';
import FilterEx from "@/lib/badWordsUtils";
import { ToastError, ToastMessage } from "@/lib/sonnerUtils";
import HttpStatus from "@/lib/HttpStatus";
import { Loader2 } from 'lucide-react';
import MdEditor from 'react-markdown-editor-lite';
import 'react-markdown-editor-lite/lib/index.css';
import { MarkdownViewer } from './articleEditorMarkdown';
import ColumnsSelector, { VArticlesTableColumns } from "./ColumnsSelector";
import {
  getCoreRowModel,
  getFilteredRowModel,
  getPaginationRowModel,
  getSortedRowModel,
  useReactTable,
  type ColumnFiltersState,
  type SortingState,
  type VisibilityState,
} from "@tanstack/react-table"
import { articleColumns } from "@/lib/tables-defs";
import TableBlock from "./TableBlock";
import PrevNextButtons from "./PrevNextButtons";
import { CategoryInput } from "./CategoryInput";
import { IArticles } from "@/lib/interfaces";
import HeaderImageSelector from "./header-image-selector";
import Link from 'next/link';
import LoginRedirect from "@/components/LoginRedirect";
import CartaTokens from '@/../public/images/tokens_sm.png';
import { GetTokenCost } from "@/lib/tokensHandlers";
import Image from "next/image";
import AdSterraIFrameBannerAuto from "@/components/adsterra-ui/adsterra-ifram-auto";
import AdSterra320x50 from "@/components/adsterra-ui/adsterra_320x50";
import { useRouter } from 'next/navigation';
import { readStreamableValue } from "@ai-sdk/rsc";

export default function ArticlesWorkbenchWrapped() {
  const searchParams = useSearchParams();
  const [ userId, setUserId ] = useState<string>(searchParams.get('userId')?.toString() ?? '');  
  const { user, isLoading, isAuthenticated } = useKindeBrowserClient();
  const [error, setError] = useState<string | null>(null);
  const [articles, setArticles] = useState<any[]>([]);
  const [isEditing, setIsEditing] = useState(false);
  const [currentArticle, setCurrentArticle] = useState<IArticles | null>(null);
  const [ categories, setCategories ] = useState<string[]>([]);
  const [ subject, setSubject ] = useState<string>("");
  const [ thoughts, setThoughts ] = useState<string>("");
  const [ isArticleGenerating, setIsArticleGenerating ] = useState<boolean>(false);
  const [ isSaving, setIsSaving ] = useState<boolean>(false);
  const [sorting, setSorting] = useState<SortingState>([]);
  const [columnFilters, setColumnFilters] = useState<ColumnFiltersState>([]);
  const [columnVisibility, setColumnVisibility] =  useState<VisibilityState>({});
  const [ dataFetched, setDataFetched ] = useState<boolean>(false);
  const [rowSelection, setRowSelection] = useState({});
  const [selectedCategories, setSelectedCategories] = useState<string[]>([]);
  const [ articleTokensCost, setArticleTokensCost ] = useState<number>(0);
  const router = useRouter();
  const [images, setImages] = useState<string | null>(null);

  useEffect(() => {
    async function getArticleTokensCost() {
      try {
        const costResp = await GetTokenCost('ARTGEN01');
  
        if(costResp.status != HttpStatus.OK) {
          throw new Error(costResp.error!);
        }
        
        setArticleTokensCost(costResp.data);
      } catch(error) {
        setArticleTokensCost(0);
      }
    }

    getArticleTokensCost();
  }, []);

  // Load articles on mount
  useEffect(() => {
    if (!isLoading && isAuthenticated && !isEditing) {
      setUserId(user!.id);
      getArticlesAction(user!.id).then((response) => {
        if(response.status != HttpStatus.OK) {
          ToastError(response.error!);
          return;
        }

        setArticles(response.data);
        setDataFetched(true);
      })
    }
  }, [isLoading, isAuthenticated, user, isEditing]);

  const handleCreateNew = () => {
    setCurrentArticle(null); // Reset for new article
    setCategories([]);
    setIsEditing(true);
  };

  const handleEdit = async (articleId: string) => {
    try {
      const fetchResult = await fetchArticle(articleId);
      
      if(fetchResult.status != HttpStatus.OK) {
        ToastError(fetchResult.error!);
        return;
      }
      
      setCurrentArticle(fetchResult.data);
      const cat = JSON.parse(fetchResult.data.categories);
      
      setCategories(cat);
      setIsEditing(true);

    } catch(error) {
      ToastError((error as Error).message);
    }
  };

  const handleIsProfane = (text: string): string => {
    const filter = new FilterEx();

    if(filter.isProfane(text)) {
      const filteredText = filter.cleanExclude(text);
      setError("The usage of profane, offensive and inappropriate words is not allowed!");
      ToastError("The usage of profane, offensive and inappropriate words is not allowed!");
      return filteredText;
    } else {
      setError(null);
      return text;
    }
  }

  const handleCategoryChange = (newCategories: string[]) => {
    setSelectedCategories(newCategories);
  };

  const handleGenerate = async () => {

    // 1. Basic Validation
    if (!subject || !thoughts) {
      setError('Please fill in all required fields (Subject and thoughts).');
      return;
    }

    // Reset states
    setIsArticleGenerating(true);
    setError(null);
    setCurrentArticle(null);
    // setCopySuccess(false);

    try {
      // const actionResponse = await initialArticleGenerationStream(subject, thoughts, userId);
      console.log('Attempting to generate...');
      // Call the Server Action securely
      const { data } = await initialArticleGenerationStream(subject, thoughts, userId);

      console.log('Started generation...');
      // Read the streamable value chunks directly
      for await (const delta of readStreamableValue(data)) {
        console.log('Getting delta...');
        if (delta) {
          // delta is a perfectly parsed, partial JSON object!
          // No manual JSON.parse strings or broken braces to worry about.
          setCurrentArticle(delta as IArticles);
          setCategories((delta as IArticles).categories);
          console.log('Delta: ', delta);
        } else {
          console.log('Delta is blank');
        }
      }
    
      router.refresh();
      window.dispatchEvent(new Event('tokens-deducted'));
    } catch (err) {
      setError('An error occurred. Error: ' + (err as Error).message);
    } finally {
      setIsArticleGenerating(false);
    }
  };

  useEffect(() => {
    console.log("Current Article: ", currentArticle);
  }, [currentArticle]);

  const table = useReactTable({
    data: articles,
    columns: articleColumns,
    onSortingChange: setSorting,
    onColumnFiltersChange: setColumnFilters,
    getCoreRowModel: getCoreRowModel(),
    getPaginationRowModel: getPaginationRowModel(),
    getSortedRowModel: getSortedRowModel(),
    getFilteredRowModel: getFilteredRowModel(),
    onColumnVisibilityChange: setColumnVisibility,
    onRowSelectionChange: setRowSelection,
    state: {
      sorting,
      columnFilters,
      columnVisibility,
      rowSelection
    },
    meta: {
      handleEdit: handleEdit
    }
  });

  return (
    <>
      <div className="space-y-8">
        <div className='text-lg'>
          <Link className='underline' href={"/"}>Home</Link>
          <span className='pl-2 pr-2'>{">"}</span>
          <Link className='underline' href={"/dashboard"}>Dashbord</Link>
          <span className='pl-2 pr-2'>{"> Articles Workbench"}</span>
        </div>

        <h1 className="text-3xl font-bold text-gray-800 dark:text-gray-100">
          Articles Workbench
        </h1>

        <AdSterraIFrameBannerAuto position="top"/>        

        {/* Error Message Display */}
        {error && (
          <div className="bg-red-100 border-l-4 border-red-500 text-red-700 p-4 rounded-md shadow-sm" role="alert">
            <p className="font-bold">Generation Failed</p>
            <p>{error}</p>
          </div>
        )}

        {
          isEditing ? 
            <div className="space-y-4">
              <div className="flex justify-between items-center">
                <button onClick={() => setIsEditing(false)} className="text-gray-500 hover:underline">
                  ← Back to List
                </button>
                <div className="flex gap-2">
                  <button 
                      onClick={async () => {
                        if(currentArticle && currentArticle.title != null && currentArticle.title !== "" 
                          && currentArticle.content != null && currentArticle.content !== "") {
                            const response = await saveArticleAction(null, currentArticle.title, currentArticle.content, currentArticle.categories, userId);
                            if(response.status != HttpStatus.OK) ToastError('Article saving faile. Error: ' + response.error);
                            else ToastMessage('Article was saved successfully.');
                            
                          }
                          else ToastError('There is no article to save or article component missing. Please, check the article title and content.');
                        }} 
                      className="bg-green-600 text-white px-4 py-2 rounded-md"
                  >
                    {isSaving ? (
                        <><Loader2 className="mr-2 h-4 w-4 animate-spin" /> Saving Article...</>
                    ) : (
                        'Save Article'
                    )}
                  </button>
                </div>
              </div>
              <div className="space-y-2">
                <p className="text-gray-600 dark:text-gray-400 mb-4 font-medium">
                  Input your context below to let the AI draft a perfect email for you.
                </p>
                <Label htmlFor="subject" className='text-indigo-600'>Subject</Label>
                <Input type="text" placeholder="Your article's subject..." value={subject}
                  name="subject"
                  id="subject"
                  className="bg-white"
                  onChange={(e) => {
                    let newSubject = handleIsProfane(e.currentTarget.value);
                    setSubject(newSubject);
                    if(currentArticle) setCurrentArticle(null);
                  }} disabled={false} />
              </div>
              <div className="space-y-2">
                <Label htmlFor="thoughts" className='text-indigo-600'>Your thoughts</Label>
                <Textarea
                  id="thoughts"
                  name="thoughts"
                  className="bg-white"
                  placeholder="What do you want the article to talk about and/or contain?"
                  value={thoughts}
                  onChange={(e) => {
                      let newThoughts = handleIsProfane(e.currentTarget.value);
                      setThoughts(newThoughts);
                      if(currentArticle) setCurrentArticle(null);
                    }
                  }
                  rows={7}
                  disabled={false}
                />
              </div>

              <CategoryInput categories={categories} 
                onChange={handleCategoryChange}
                setCategories={setCategories}
              />
              
              <div>
                {/* Generate Button (with loading state) */}
                <Button 
                    onClick={handleGenerate} 
                    className="w-full bg-indigo-600 hover:bg-indigo-700 text-white transition duration-200"
                    disabled={isArticleGenerating}
                >
                  <div className="flex flex-row justify-center items-center gap-2">
                    <div className="text-lg">
                      {isArticleGenerating ? (
                        <div className="flex justify-center items-center gap-2">
                          <Loader2 className="mr-2 h-4 w-4 animate-spin" /> 
                          <div>Generating Article...</div>
                        </div>

                    ) : (
                      <div className="flex justify-center items-center gap-2">
                        <div>
                          Generate Article
                        </div>
                        <div className="flex flex-row justify-center items-center gap-1 text-sm">
                          <span>{"("}</span><Image src={CartaTokens} width={20} height={20} alt="Carta AI Tokens" />
                          <span className="ml-1">{articleTokensCost} tokens</span><span>{")"}</span>
                        </div>
                      </div>
                    )}
                    </div>
                  </div>
                </Button>
              </div>
              <div>
                <span className="text-red-600">{error}</span>
              </div>
              
              <hr/>

              {/* <AdSterraIFrameBannerAuto position="middle1" /> */}
              <AdSterra320x50 />

              <hr />

              <Label className='text-indigo-600'>Your generated article</Label>

              {/* <ArticleEditor initialContent={currentArticle ? currentArticle?.content || [] : null} /> */}

              <MdEditor
                key={currentArticle ? crypto.randomUUID() : "empty"}
                value={currentArticle?.content ?? ""}
                style={{ height: '600px', width: '100%' }}
                // 1. This is the magic part: pass the text to your custom viewer
                renderHTML={(text) => <MarkdownViewer content={text} />}
                // onChange={({ text }) => onChange(text)}
                // 2. Optional: Configure the toolbar to your liking
                config={{
                  view: {
                    menu: true,
                    md: true,
                    html: true,
                  },
                  canView: {
                    fullScreen: true,
                    hideMenu: true,
                  }
                }}
              />
              <hr />

              {/* <HeaderImagePicker topic={subject} /> */}
              <HeaderImageSelector topic={subject} images={images} setImages={setImages} />
            </div>
          : <div className="p-6">
              <div className="flex justify-between items-center mb-8">
                <h1 className="text-2xl font-bold">My Articles</h1>
                <button 
                  onClick={handleCreateNew}
                  className="bg-indigo-600 text-white px-4 py-2 rounded-lg hover:bg-indigo-700 transition"
                >
                  + New Article
                </button>
              </div>

              <div className="w-full">
                <div className="flex items-center py-4">
                  <Input
                    placeholder="Filter Drafts..."
                    value={(table.getColumn("title")?.getFilterValue() as string) ?? ""}
                    onChange={(event) =>
                      table.getColumn("title")?.setFilterValue(event.target.value)
                    }
                    className="max-w-sm"
                  />
                  <ColumnsSelector table={table} VArticlesTableColumns={VArticlesTableColumns} />
                  
                </div>
                
                <TableBlock isLoading={isLoading} dataFetched={dataFetched} table={table} />
                        
                <PrevNextButtons table={table} />
                
              </div>
            </div>
        }
      </div>

      <AdSterraIFrameBannerAuto position="bottom" />

      {/* 2. THE OVERLAY (Only shows if not logged in) */}
      {(!isAuthenticated && !isLoading) && (
        <LoginRedirect message="to analyze this job description" />
      )}
    </>
  )
}